import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Share2, Copy, Check, Loader2, X } from "lucide-react";

const ShareCookbookDialog = ({ open, onClose, defaultTitle }) => {
  const [title, setTitle] = useState(defaultTitle || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { share_id, url, title }
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const createShare = async () => {
    setLoading(true);
    try {
      const res = await api.post("/cookbook/share", { title: title.trim() || null });
      const shareUrl = `${window.location.origin}/cookbook/shared/${res.data.share_id}`;
      setResult({ share_id: res.data.share_id, url: shareUrl, title: res.data.title });
      toast.success("Cookbook link ready");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't create the link";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the link manually");
    }
  };

  const reset = () => {
    setResult(null);
    setCopied(false);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-stone-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-cookbook-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg bg-stone-950 border border-stone-800 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="share-cookbook-close"
          className="absolute top-4 right-4 text-stone-500 hover:text-orange-500"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Share2 className="w-4 h-4 text-orange-500" />
            <span className="eyebrow">Share the cookbook</span>
          </div>

          {!result ? (
            <>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-[0.95] tracking-tighter text-stone-100">
                Send your <span className="italic text-orange-600">whole shelf</span>
                <br />
                to a mate.
              </h2>
              <p className="mt-4 text-sm text-stone-400 leading-relaxed">
                We&apos;ll bundle every recipe in your cookbook into one shareable link,
                cover photo and all. They won&apos;t need an account to read it.
              </p>

              <label className="block mt-8 mb-2 text-[10px] uppercase tracking-[0.24em] text-stone-500">
                Give it a title (optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder={defaultTitle || "e.g. Sunday supper picks"}
                data-testid="share-cookbook-title"
                className="w-full bg-stone-900 border border-stone-800 text-stone-100 px-4 py-3 outline-none focus:border-orange-600 font-serif-display text-lg"
              />

              <div className="mt-8 flex items-center gap-4">
                <button
                  type="button"
                  onClick={createShare}
                  disabled={loading}
                  data-testid="share-cookbook-create"
                  className="inline-flex items-center gap-3 bg-orange-700 hover:bg-orange-800 disabled:opacity-60 text-stone-50 px-8 py-3.5 uppercase tracking-[0.24em] text-xs font-medium transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Bundling
                    </>
                  ) : (
                    <>Generate link</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 ink-link"
                >
                  Not now
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-[0.95] tracking-tighter text-stone-100">
                Right, <span className="italic text-orange-600">off it goes</span>.
              </h2>
              <p className="mt-4 text-sm text-stone-400 leading-relaxed">
                &ldquo;{result.title}&rdquo; is now live. Anyone with this link can read every
                recipe. Post it, message it, print it on a tea-towel &mdash; up to you.
              </p>

              <div className="mt-8 border border-stone-800 bg-stone-900/60 p-4 flex items-center gap-3">
                <input
                  type="text"
                  readOnly
                  value={result.url}
                  onFocus={(e) => e.target.select()}
                  data-testid="share-cookbook-url"
                  className="flex-1 bg-transparent text-stone-200 outline-none text-sm font-mono-accent truncate"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="share-cookbook-copy"
                  className="inline-flex items-center gap-2 bg-orange-700 hover:bg-orange-800 text-stone-50 px-4 py-2 uppercase tracking-[0.24em] text-[10px] font-medium transition-colors"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" /> Copy
                    </>
                  )}
                </button>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href={result.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-xs uppercase tracking-[0.24em] text-orange-500 hover:text-orange-400 ink-link"
                >
                  Preview it →
                </a>
                <button
                  type="button"
                  onClick={reset}
                  className="text-xs uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 ink-link"
                >
                  Make another
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="ml-auto text-xs uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 ink-link"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareCookbookDialog;
